import React, { useEffect, useMemo, useState, useRef } from "react";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import AceAvatar from "../../../components/shared/AceAvatar";
import { useAppPreferences } from "../../../context/AppPreferencesContext";
import { auth, db } from "../../../firebase/firebase";

import {
  getAssignedClasses,
  getCurrentUser,
  requestPasswordReset,
} from "../../../utils/auth";

import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updateEmail,
  updatePassword as firebaseUpdatePassword,
  updateProfile,
} from "firebase/auth";

const AUTH_KEY = "ac_admin_user";
const CLASS_OPTIONS = ["1AD", "2AD", "3AD", "4AD", "5AD", "Andere"];
const MAX_CLASSES = 3;

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function formatText(template, replacements = {}) {
  return String(template).replace(/\{\{(\w+)\}\}/g, (_, key) => {
    return replacements[key] != null ? String(replacements[key]) : `{{${key}}}`;
  });
}

function formatDate(value) {
  if (!value) return "—";

  if (value?.toDate && typeof value.toDate === "function") {
    return value.toDate().toLocaleDateString("de-AT");
  }

  if (typeof value === "string") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString("de-AT");
    }
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toLocaleDateString("de-AT");
  }

  return "—";
}

function syncLocalUser(updatedUser) {
  localStorage.setItem(AUTH_KEY, JSON.stringify(updatedUser));
}

function notify(message, type = "success") {
  if (typeof window !== "undefined" && typeof window.showToast === "function") {
    window.showToast(message, type);
    return;
  }

  window.alert(message);
}

function notifyPendingChange() {
  notify(
    "Änderung vorgemerkt. Erst nach dem Speichern bleibt sie erhalten.",
    "success"
  );
}

function Toggle({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onChange}
      className={`ac-settings-toggle ${checked ? "is-on" : ""} ${
        disabled ? "is-disabled" : ""
      }`}
      aria-pressed={checked}
      disabled={disabled}
    >
      <span className={`ac-settings-toggle-knob ${checked ? "is-on" : ""}`} />
    </button>
  );
}

function RecipientChip({ mail, onRemove, removeTitle }) {
  return (
    <div className="ac-settings-chip">
      <span>{mail}</span>
      <button
        type="button"
        onClick={onRemove}
        className="ac-settings-chip-remove"
        title={removeTitle}
        aria-label={`${removeTitle}: ${mail}`}
      >
        ×
      </button>
    </div>
  );
}

function InitialsAvatar({ name }) {
  const initials = useMemo(() => {
    const parts = String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (parts.length === 0) return "A";
    if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();

    return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
  }, [name]);

  return <div className="ac-settings-useravatar">{initials}</div>;
}

function PasswordRule({ ok, text }) {
  return (
    <div className={`ac-settings-ruleitem ${ok ? "is-ok" : "is-off"}`}>
      <span className="ac-settings-ruleicon">{ok ? "✓" : "✕"}</span>
      <span className="ac-settings-ruletext">{text}</span>
    </div>
  );
}

function SettingsNavIcon({ name }) {
  if (name === "profil") {
    return (
      <svg viewBox="0 0 24 24" className="ac-settings-navsvg" aria-hidden="true">
        <circle
          cx="12"
          cy="8.3"
          r="3.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="M5.8 18c1.1-3.1 3.5-4.8 6.2-4.8s5.1 1.7 6.2 4.8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  if (name === "sicherheit") {
    return (
      <svg viewBox="0 0 24 24" className="ac-settings-navsvg" aria-hidden="true">
        <path
          d="M8 10V7.8a4 4 0 1 1 8 0V10"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <rect
          x="5.5"
          y="10"
          width="13"
          height="9"
          rx="2.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
      </svg>
    );
  }

  if (name === "benachrichtigungen") {
    return (
      <svg viewBox="0 0 24 24" className="ac-settings-navsvg" aria-hidden="true">
        <path
          d="M12 4.5a4 4 0 0 1 4 4v2.2c0 1 .3 2 .9 2.8l1 1.4c.5.8 0 1.9-1 1.9H7.1c-1 0-1.5-1.1-1-1.9l1-1.4c.6-.8.9-1.8.9-2.8V8.5a4 4 0 0 1 4-4Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M10 18.5a2.2 2.2 0 0 0 4 0"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" className="ac-settings-navsvg" aria-hidden="true">
      <circle
        cx="12"
        cy="12"
        r="3.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M12 3.8v2.1M12 18.1v2.1M20.2 12h-2.1M5.9 12H3.8M17.9 6.1l-1.5 1.5M7.6 16.4l-1.5 1.5M17.9 17.9l-1.5-1.5M7.6 7.6 6.1 6.1"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function formatRequestedClassesLabel(classes) {
  if (!Array.isArray(classes) || classes.length === 0) return "";
  return classes.join(", ");
}

export default function AccountSettingsPage() {
  const {
    themePreference,
    resolvedTheme,
    setThemePreference,
    themeOptions,
  } = useAppPreferences();

  const TEXT = {
    pageTitle: "Einstellungen",
    heroTitle: "Einstellungen",
    heroText:
      "Verwalte hier dein Profil, Sicherheit, Benachrichtigungen und Systemoptionen.",
    aceSays: "Halte alles klar, ruhig und gut organisiert.",
    navTitle: "Bereiche",

    tabs: {
      profil: {
        navLabel: "Profil",
        navSub: "Persönliche Daten und Konto",
        title: "Profil",
        subtitle: "Passe deine persönlichen Kontodaten an.",
      },
      sicherheit: {
        navLabel: "Sicherheit",
        navSub: "Passwort und Schutz",
        title: "Sicherheit",
        subtitle: "Schütze dein Konto mit einem sicheren Passwort.",
      },
      benachrichtigungen: {
        navLabel: "Benachrichtigungen",
        navSub: "Mails und Hinweise",
        title: "Benachrichtigungen",
        subtitle: "Lege fest, welche Hinweise per Mail versendet werden.",
      },
      system: {
        navLabel: "System",
        navSub: "Sprache und Design",
        title: "System",
        subtitle: "Passe die Darstellung des Admin Panels an.",
      },
    },

    common: {
      cancel: "Abbrechen",
      add: "Hinzufügen",
      remove: "Entfernen",
      show: "Anzeigen",
      hide: "Verbergen",
    },

    profile: {
      personalInfoTitle: "Persönliche Informationen",
      personalInfoText: "Bearbeite Name und E-Mail-Adresse deines Kontos.",
      accountActive: "Konto aktiv",
      displayName: "Anzeigename",
      accountEmail: "Konto-E-Mail",
      role: "Rolle",
      memberSince: "Mitglied seit",
      Badge: "Administrator",
      profileHint: "Diese Angaben werden für dein Admin-Konto verwendet.",
      saveButton: "Profil speichern",
    },

    security: {
      passwordProtectionTitle: "Passwortschutz",
      passwordProtectionText:
        "Ändere dein Passwort und verbessere die Kontosicherheit.",
      safetyHint:
        "Nutze ein starkes Passwort mit mindestens 8 Zeichen, Zahl und Sonderzeichen.",
      changePassword: "Passwort ändern",
      forgotPassword: "Aktuelles Passwort vergessen?",
      forgotPasswordHint:
        "Falls du dein aktuelles Passwort nicht mehr kennst, kannst du dir einen Reset-Link per E-Mail senden lassen.",
      forgotPasswordInfo:
        "Wir senden einen sicheren Link an deine Konto-E-Mail, damit du dein Passwort zurücksetzen kannst.",
      sendResetLink: "Reset-Link senden",
      currentPassword: "Aktuelles Passwort",
      currentPasswordPlaceholder: "Aktuelles Passwort eingeben",
      newPassword: "Neues Passwort",
      newPasswordPlaceholder: "Neues Passwort eingeben",
      confirmPassword: "Passwort bestätigen",
      confirmPasswordPlaceholder: "Neues Passwort wiederholen",
      confirmMismatch: "Die Passwörter stimmen nicht überein.",
      securityCheckTitle: "Sicherheitscheck",
      securityCheckText:
        "Prüfe, ob dein neues Passwort die wichtigsten Regeln erfüllt.",
      score: "Score: {{score}} / 5",
      length: "Mindestens 8 Zeichen",
      number: "Mindestens eine Zahl",
      special: "Mindestens ein Sonderzeichen",
      different: "Nicht identisch mit dem aktuellen Passwort",
      confirm: "Bestätigung stimmt überein",
      saveButton: "Passwort speichern",
    },

    notifications: {
      optionalTitle: "Optionale Benachrichtigungen",
      optionalText:
        "Diese Hinweise kannst du flexibel ein- oder ausschalten.",
      optionalActiveCount: "{{count}} aktiv",
      criticalStressTitle: "Kritische Stresswarnungen",
      criticalStressText:
        "Mail bei auffälligen Stresswerten einzelner Nutzer:innen.",
      streakTitle: "Streak-Hinweise",
      streakText:
        "Mail, wenn längere Aktivitätsserien oder Abbrüche erkannt werden.",
      summariesTitle: "Zusammenfassungen",
      summariesText:
        "Regelmäßige Übersichten über wichtige Systemereignisse.",
      feedbackTitle: "Neues Feedback",
      feedbackText:
        "Mail bei neuen Rückmeldungen oder offenen Antworten.",
      technicalTitle: "Technische Hinweise",
      technicalText:
        "Mail bei technischen Problemen oder internen Fehlern.",
      saveButton: "Benachrichtigungen speichern",
      mainAddressTitle: "Hauptadresse",
      mainAddressText:
        "An diese Adresse werden wichtige Benachrichtigungen gesendet.",
      requiredAddress: "Pflicht",
      mainAddressLabel: "Admin-Mailadresse",
      requiredHint:
        "Diese Adresse sollte immer erreichbar sein und wird für wichtige Meldungen verwendet.",
      fixedTitle: "Fixe Systemmeldungen",
      fixedText:
        "Diese Mails bleiben immer aktiv und können nicht deaktiviert werden.",
      locked: "Fix",
      systemOutageTitle: "Systemausfall",
      systemOutageText:
        "Wird bei Ausfällen oder kritischen Störungen sofort gemeldet.",
      securityIncidentTitle: "Sicherheitsvorfall",
      securityIncidentText:
        "Wird bei verdächtigen Login- oder Sicherheitsereignissen gesendet.",
      deliveryIssueTitle: "Zustellproblem",
      deliveryIssueText:
        "Wird gemeldet, wenn interne Benachrichtigungen nicht zugestellt werden konnten.",
      fixedBadge: "Immer aktiv",
      recipientsTitle: "Weitere Empfänger:innen",
      recipientsText:
        "Zusätzliche Mailadressen für interne Hinweise und Team-Infos.",
      recipientsStoredCount: "{{count}} gespeichert",
      addRecipientPlaceholder: "weitere.mail@acetime.at",
      noRecipients: "Noch keine zusätzlichen Empfänger:innen vorhanden.",
    },

    system: {
      themeTitle: "Darstellung",
      themeText: "Wähle, wie das Admin Panel dargestellt werden soll.",
      themeResolvedAuto: "Systemmodus aktiv · aktuell: {{theme}}",
      themeResolvedManual: "Aktives Design: {{theme}}",
      saveButton: "System speichern",
    },

    messages: {
      loadFailed: "Einstellungen konnten nicht geladen werden.",
      notLoggedIn: "Kein eingeloggter Admin gefunden.",
      saveBlockedRules:
        "Die Daten konnten nicht in Firestore gespeichert werden. Prüfe deine Rules für /admins/{uid}.",
      enterEmailFirst: "Bitte zuerst eine E-Mail-Adresse eingeben.",
      enterValidRecipient:
        "Bitte eine gültige zusätzliche E-Mail-Adresse eingeben.",
      recipientAlreadyExists:
        "Diese E-Mail-Adresse ist bereits vorhanden.",
      removeRecipientConfirm:
        "Soll „{{mail}}“ wirklich entfernt werden?",
      enterName: "Bitte einen Namen eingeben.",
      enterValidEmail: "Bitte eine gültige Konto-E-Mail eingeben.",
      enterValidAdminEmail:
        "Bitte eine gültige Admin-Mailadresse eingeben.",
      enterValidResetEmail:
        "Für den Reset wird eine gültige Konto-E-Mail benötigt.",
      settingsSaved: "Die Profileinstellungen wurden gespeichert.",
      fillPasswordFields:
        "Bitte alle Passwortfelder vollständig ausfüllen.",
      passwordRequirements:
        "Das neue Passwort erfüllt noch nicht alle Anforderungen.",
      wrongCurrentPassword: "Das aktuelle Passwort ist falsch.",
      passwordChanged: "Das Passwort wurde erfolgreich geändert.",
      passwordChangeFailed:
        "Das Passwort konnte gerade nicht geändert werden.",
      emailUpdateNeedsRecentLogin:
        "Die E-Mail konnte nicht geändert werden. Bitte melde dich neu an und versuche es erneut.",
      resetLinkSent:
        "Wenn Firebase den Versand akzeptiert hat, wurde die Reset-Mail gesendet. Bitte auch Spam prüfen.",
      resetLinkFailed: "Der Reset-Link konnte gerade nicht gesendet werden.",
      notificationsSaved: "Die Benachrichtigungseinstellungen wurden gespeichert.",
      systemSaved: "Die Systemeinstellungen wurden gespeichert.",
    },
  };

  const localUser = useMemo(() => getCurrentUser(), []);
  const didLoadSettingsRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profil");

  const [uid, setUid] = useState(localUser?.uid || "");
  const [name, setName] = useState("AceTime Admin");
  const [email, setEmail] = useState("admin@acetime.at");
  const [role, setRole] = useState("admin");
  const [memberSince, setMemberSince] = useState("—");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [adminAlertMail, setAdminAlertMail] = useState("admin@acetime.at");
  const [adminAlertsEnabled, setAdminAlertsEnabled] = useState(true);
  const [summaryMailEnabled, setSummaryMailEnabled] = useState(true);
  const [technicalMailEnabled, setTechnicalMailEnabled] = useState(true);
  const [feedbackMailEnabled, setFeedbackMailEnabled] = useState(true);
  const [streakMailEnabled, setStreakMailEnabled] = useState(true);

  const [extraRecipients, setExtraRecipients] = useState([]);
  const [newRecipientMail, setNewRecipientMail] = useState("");

  const [selectedClasses, setSelectedClasses] = useState([]);
  const [customClass, setCustomClass] = useState("");
  const [classMessage, setClassMessage] = useState("");
  const [classError, setClassError] = useState("");
  const [classSuccess, setClassSuccess] = useState("");

  const assignedClasses = useMemo(
    () => getAssignedClasses(localUser),
    [localUser]
  );

  const hasOtherClass = selectedClasses.includes("Andere");

  const normalizedRequestedClasses = useMemo(() => {
    const fixedClasses = selectedClasses
      .filter((entry) => entry !== "Andere")
      .map((entry) => String(entry || "").trim())
      .filter(Boolean);

    if (hasOtherClass && customClass.trim()) {
      fixedClasses.push(customClass.trim());
    }

    return fixedClasses.filter(Boolean);
  }, [selectedClasses, customClass, hasOtherClass]);


  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let mounted = true;

    async function loadAdminSettings() {
      if (didLoadSettingsRef.current) {
        if (mounted) setLoading(false);
        return;
      }

      try {
        const firebaseUser = auth.currentUser;
        const resolvedUid = firebaseUser?.uid || localUser?.uid || "";
        const resolvedEmail =
          firebaseUser?.email || localUser?.email || "admin@acetime.at";

        if (!resolvedUid) {
          if (mounted) setLoading(false);
          return;
        }

        const adminRef = doc(db, "admins", resolvedUid);
        const adminSnap = await getDoc(adminRef);
        const adminData = adminSnap.exists() ? adminSnap.data() : {};

        const notificationPrefs = adminData?.settings?.notifications || {};
        const systemPrefs = adminData?.settings?.system || {};

        if (!mounted) return;

        didLoadSettingsRef.current = true;

        setUid(resolvedUid);
        setName(
          adminData?.name ||
            firebaseUser?.displayName ||
            localUser?.name ||
            "AceTime Admin"
        );
        setEmail(adminData?.email || resolvedEmail);
        setRole(adminData?.role || localUser?.role || "admin");
        setMemberSince(
          formatDate(adminData?.createdAt || firebaseUser?.metadata?.creationTime)
        );

        setAdminAlertMail(
          notificationPrefs.mainAddress ||
            adminData?.email ||
            resolvedEmail ||
            "admin@acetime.at"
        );
        setAdminAlertsEnabled(notificationPrefs.adminAlertsEnabled ?? true);
        setSummaryMailEnabled(notificationPrefs.summaryMailEnabled ?? true);
        setTechnicalMailEnabled(notificationPrefs.technicalMailEnabled ?? true);
        setFeedbackMailEnabled(notificationPrefs.feedbackMailEnabled ?? true);
        setStreakMailEnabled(notificationPrefs.streakMailEnabled ?? true);
        setExtraRecipients(
          Array.isArray(notificationPrefs.extraRecipients)
            ? notificationPrefs.extraRecipients
            : []
        );

        if (systemPrefs.theme && !localStorage.getItem("ac_admin_theme_preference")) {
          setThemePreference(systemPrefs.theme);
        }
      } catch (error) {
        console.error("Account settings load failed:", error);
        notify(TEXT.messages.loadFailed, "error");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadAdminSettings();

    return () => {
      mounted = false;
    };
  }, [
    localUser?.uid,
    localUser?.email,
    localUser?.name,
    localUser?.role,
    setThemePreference,
    TEXT.messages.loadFailed,
  ]);

  const tabMeta = {
    profil: {
      title: TEXT.tabs.profil.title,
      subtitle: TEXT.tabs.profil.subtitle,
    },
    sicherheit: {
      title: TEXT.tabs.sicherheit.title,
      subtitle: TEXT.tabs.sicherheit.subtitle,
    },
    benachrichtigungen: {
      title: TEXT.tabs.benachrichtigungen.title,
      subtitle: TEXT.tabs.benachrichtigungen.subtitle,
    },
    system: {
      title: TEXT.tabs.system.title,
      subtitle: TEXT.tabs.system.subtitle,
    },
  };

  const navItems = [
    {
      key: "profil",
      title: TEXT.tabs.profil.navLabel,
      sub: TEXT.tabs.profil.navSub,
    },
    {
      key: "sicherheit",
      title: TEXT.tabs.sicherheit.navLabel,
      sub: TEXT.tabs.sicherheit.navSub,
    },
    {
      key: "benachrichtigungen",
      title: TEXT.tabs.benachrichtigungen.navLabel,
      sub: TEXT.tabs.benachrichtigungen.navSub,
    },
    {
      key: "system",
      title: TEXT.tabs.system.navLabel,
      sub: TEXT.tabs.system.navSub,
    },
  ];

  const confirmMismatch =
    confirmPassword.length > 0 && newPassword !== confirmPassword;

  const passwordRules = useMemo(
    () => ({
      length: newPassword.length >= 8,
      number: /\d/.test(newPassword),
      special: /[^A-Za-z0-9]/.test(newPassword),
      differentFromCurrent:
        currentPassword.length > 0 &&
        newPassword.length > 0 &&
        currentPassword !== newPassword,
      confirm:
        confirmPassword.length > 0 && newPassword === confirmPassword,
    }),
    [currentPassword, newPassword, confirmPassword]
  );

  const passwordScore = Object.values(passwordRules).filter(Boolean).length;
  const passwordReady = Object.values(passwordRules).every(Boolean);

  const activeOptionalAlerts = [
    adminAlertsEnabled,
    summaryMailEnabled,
    technicalMailEnabled,
    feedbackMailEnabled,
    streakMailEnabled,
  ].filter(Boolean).length;

  const aceVariant = useMemo(() => {
    if (confirmMismatch) return "stress1";
    if (activeOptionalAlerts >= 4 && extraRecipients.length >= 2) {
      return "motiviert2";
    }
    if (activeOptionalAlerts >= 2) return "ruhig2";
    return "ruhig1";
  }, [confirmMismatch, activeOptionalAlerts, extraRecipients.length]);


  const themeLabel = (code) => {
    const map = {
      system: "System",
      light: "Hell",
      dark: "Dunkel",
    };
    return map[code] || code;
  };

  const themeDescription = (code) => {
    const map = {
      system: "Übernimmt automatisch das Design deines Geräts.",
      light: "Helles, ruhiges Layout für den Standardbetrieb.",
      dark: "Dunkler Modus für reduzierte Blendung.",
    };
    return map[code] || code;
  };

  const resolvedThemeLabel = themeLabel(resolvedTheme);

  const toggleOptionalNotification = (label, value, setter) => {
    setter(!value);
    notifyPendingChange();
  };

  const addRecipient = () => {
    const trimmed = newRecipientMail.trim().toLowerCase();

    if (!trimmed) {
      notify(TEXT.messages.enterEmailFirst, "error");
      return;
    }

    if (!isValidEmail(trimmed)) {
      notify(TEXT.messages.enterValidRecipient, "error");
      return;
    }

    if (
      trimmed === adminAlertMail.trim().toLowerCase() ||
      extraRecipients.some((item) => item.toLowerCase() === trimmed)
    ) {
      notify(TEXT.messages.recipientAlreadyExists, "error");
      return;
    }

    setExtraRecipients((prev) => [...prev, trimmed]);
    setNewRecipientMail("");
    notifyPendingChange();
  };

  const removeRecipient = (mail) => {
    const confirmed = window.confirm(
      formatText(TEXT.messages.removeRecipientConfirm, { mail })
    );

    if (!confirmed) return;

    setExtraRecipients((prev) => prev.filter((item) => item !== mail));
    notifyPendingChange();
  };

  function toggleClass(className) {
    setClassError("");
    setClassSuccess("");

    if (assignedClasses.includes(className)) return;

    setSelectedClasses((prev) => {
      const exists = prev.includes(className);

      if (exists) {
        const next = prev.filter((entry) => entry !== className);
        if (className === "Andere") {
          setCustomClass("");
        }
        return next;
      }

      if (assignedClasses.length + prev.length >= MAX_CLASSES) {
        setClassError(`Maximal ${MAX_CLASSES} Klassen erlaubt.`);
        return prev;
      }

      return [...prev, className];
    });
  }

  async function saveAdminDoc(overrides = {}) {
    if (!uid) {
      throw new Error(TEXT.messages.notLoggedIn);
    }

    const adminRef = doc(db, "admins", uid);

    await setDoc(
      adminRef,
      {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        role,
        active: true,
        updatedAt: serverTimestamp(),
        settings: {
          notifications: {
            mainAddress: adminAlertMail.trim().toLowerCase(),
            adminAlertsEnabled,
            summaryMailEnabled,
            technicalMailEnabled,
            feedbackMailEnabled,
            streakMailEnabled,
            extraRecipients,
          },
          system: {
            theme: themePreference,
          },
        },
        ...overrides,
      },
      { merge: true }
    );
  }

  const saveProfile = async () => {
    const firebaseUser = auth.currentUser;
    const cleanName = name.trim();
    const cleanEmail = email.trim().toLowerCase();

    if (!cleanName) {
      notify(TEXT.messages.enterName, "error");
      return;
    }

    if (!isValidEmail(cleanEmail)) {
      notify(TEXT.messages.enterValidEmail, "error");
      return;
    }

    try {
      if (firebaseUser) {
        if (firebaseUser.displayName !== cleanName) {
          await updateProfile(firebaseUser, { displayName: cleanName });
        }

        if (
          firebaseUser.email &&
          firebaseUser.email.toLowerCase() !== cleanEmail
        ) {
          try {
            await updateEmail(firebaseUser, cleanEmail);
          } catch (error) {
            console.error("Email update failed:", error);
            notify(TEXT.messages.emailUpdateNeedsRecentLogin, "error");
            return;
          }
        }
      }

      await saveAdminDoc({
        name: cleanName,
        email: cleanEmail,
      });

      syncLocalUser({
        uid,
        email: cleanEmail,
        name: cleanName,
        role,
        assignedClasses,
      });

      setName(cleanName);
      setEmail(cleanEmail);

      notify(TEXT.messages.settingsSaved, "success");
    } catch (error) {
      console.error("Profile save failed:", error);
      notify(TEXT.messages.saveBlockedRules, "error");
    }
  };

  const savePassword = async () => {
    const firebaseUser = auth.currentUser;

    if (!firebaseUser || !firebaseUser.email) {
      notify(TEXT.messages.notLoggedIn, "error");
      return;
    }

    if (!currentPassword || !newPassword || !confirmPassword) {
      notify(TEXT.messages.fillPasswordFields, "error");
      return;
    }

    if (!passwordReady) {
      notify(TEXT.messages.passwordRequirements, "error");
      return;
    }

    try {
      const credential = EmailAuthProvider.credential(
        firebaseUser.email,
        currentPassword
      );

      await reauthenticateWithCredential(firebaseUser, credential);
      await firebaseUpdatePassword(firebaseUser, newPassword);

      notify(TEXT.messages.passwordChanged, "success");

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Password update failed:", error);
      notify(
        error?.code === "auth/invalid-credential" ||
          error?.code === "auth/wrong-password"
          ? TEXT.messages.wrongCurrentPassword
          : TEXT.messages.passwordChangeFailed,
        "error"
      );
    }
  };

  const sendPasswordResetFromSettings = async () => {
    const firebaseEmail =
      auth.currentUser?.email || String(email || "").trim().toLowerCase();

    if (!isValidEmail(firebaseEmail)) {
      notify(TEXT.messages.enterValidResetEmail, "error");
      return;
    }

    const result = await requestPasswordReset(firebaseEmail);

    if (!result.ok) {
      notify(result.error || TEXT.messages.resetLinkFailed, "error");
      return;
    }

    notify(TEXT.messages.resetLinkSent, "success");
  };

  const saveNotifications = async () => {
    const cleanAdminAlertMail = adminAlertMail.trim().toLowerCase();

    if (!isValidEmail(cleanAdminAlertMail)) {
      notify(TEXT.messages.enterValidAdminEmail, "error");
      return;
    }

    try {
      await saveAdminDoc();
      setAdminAlertMail(cleanAdminAlertMail);
      notify(TEXT.messages.notificationsSaved, "success");
    } catch (error) {
      console.error("Notification settings save failed:", error);
      notify(TEXT.messages.saveBlockedRules, "error");
    }
  };

  const saveSystemSettings = async () => {
    try {
      await saveAdminDoc();
      notify(TEXT.messages.systemSaved, "success");
    } catch (error) {
      console.error("System settings save failed:", error);
      notify(TEXT.messages.saveBlockedRules, "error");
    }
  };

  const renderTeacherClassSection =
    (role === "teacher" || role === "viewer") && (
      <div className="ac-settings-card" style={{ marginTop: "20px" }}>
        <div className="ac-settings-card-head">
          <div>
            <div className="ac-settings-card-title">Klassen verwalten</div>
            <div className="ac-settings-card-text">
              Sieh deine Klassen und frage zusätzliche Klassen an.
            </div>
          </div>

          <div className="ac-settings-card-badge">
            {assignedClasses.length} / {MAX_CLASSES}
          </div>
        </div>

        <div style={{ marginTop: "12px" }}>
          <label className="ac-settings-label">Deine Klassen</label>

          {assignedClasses.length === 0 ? (
            <div className="ac-settings-mutedbox">
              Keine Klassen zugewiesen.
            </div>
          ) : (
            <div className="ac-class-grid">
              {assignedClasses.map((cls) => (
                <button
                  key={cls}
                  type="button"
                  className="ac-class-chip is-active"
                  style={{ cursor: "default" }}
                  disabled
                >
                  {cls}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginTop: "16px" }}>
          <label className="ac-settings-label">Neue Klassen anfragen</label>

          <div className="ac-class-grid">
            {CLASS_OPTIONS.map((cls) => {
              const disabled = assignedClasses.includes(cls);
              const active = selectedClasses.includes(cls);

              return (
                <button
                  key={cls}
                  type="button"
                  disabled={disabled}
                  className={`ac-class-chip ${
                    disabled ? "is-disabled" : active ? "is-active" : ""
                  }`}
                  onClick={() => toggleClass(cls)}
                >
                  {cls}
                  {disabled ? " ✓" : ""}
                </button>
              );
            })}
          </div>

          <div className="ac-settings-modehint" style={{ marginTop: "8px" }}>
            Maximal {MAX_CLASSES} Klassen insgesamt. Bereits zugewiesene Klassen
            sind gesperrt.
          </div>
        </div>

        {hasOtherClass && (
          <div className="ac-settings-field" style={{ marginTop: "12px" }}>
            <label className="ac-settings-label">Andere Klasse</label>
            <input
              type="text"
              value={customClass}
              onChange={(e) => setCustomClass(e.target.value)}
              className="ac-settings-input"
              placeholder="z. B. 2BK oder 4CK"
            />
          </div>
        )}

        <div className="ac-settings-field" style={{ marginTop: "12px" }}>
          <label className="ac-settings-label">Nachricht</label>
          <textarea
            value={classMessage}
            onChange={(e) => setClassMessage(e.target.value)}
            className="ac-settings-input"
            placeholder="Optional: kurze Info, warum du zusätzliche Klassen brauchst."
            rows={4}
          />
        </div>

        {!!normalizedRequestedClasses.length && (
          <div className="ac-settings-safebox" style={{ marginTop: "12px" }}>
            Angefragt: {formatRequestedClassesLabel(normalizedRequestedClasses)}
          </div>
        )}

        {classError && (
          <div className="ac-login-error" style={{ marginTop: "12px" }}>
            <span className="ac-login-erroricon">!</span>
            <span>{classError}</span>
          </div>
        )}

        {classSuccess && (
          <div className="ac-login-success" style={{ marginTop: "12px" }}>
            <span className="ac-login-erroricon">✓</span>
            <span>{classSuccess}</span>
          </div>
        )}
        
      </div>
    );

  const renderTabContent = () => {
    if (activeTab === "profil") {
      return (
        <div className="ac-settings-pane-stack">
          <div className="ac-settings-profile-grid">
            <div className="ac-settings-card ac-settings-profilecard">
              <div className="ac-settings-profilehead">
                <InitialsAvatar name={name} />

                <div className="min-w-0">
                  <div className="ac-settings-profiletitle">{name}</div>
                  <div className="ac-settings-profilemail">{email}</div>
                </div>
              </div>

              <div className="ac-settings-badgerow">
                <span className="ac-settings-streakpill">
                  {role === "admin"
                    ? TEXT.profile.Badge
                    : role === "teacher"
                    ? "Lehrkraft"
                    : "Einsicht"}
                </span>
              </div>

              <div className="ac-settings-profilemeta">
                {TEXT.profile.memberSince}: <strong>{memberSince}</strong>
              </div>

              <div className="ac-settings-profilehint">
                {TEXT.profile.profileHint}
              </div>
            </div>

            <div className="ac-settings-card">
              <div className="ac-settings-card-head">
                <div>
                  <div className="ac-settings-card-title">
                    {TEXT.profile.personalInfoTitle}
                  </div>
                  <div className="ac-settings-card-text">
                    {TEXT.profile.personalInfoText}
                  </div>
                </div>

                <div className="ac-settings-card-badge">
                  {TEXT.profile.accountActive}
                </div>
              </div>

              <div className="ac-settings-usercard-info">
                <div className="ac-settings-infofield">
                  <label className="ac-settings-label">
                    {TEXT.profile.displayName}
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="ac-settings-input"
                    placeholder={TEXT.profile.displayName}
                  />
                </div>

                <div className="ac-settings-infofield">
                  <label className="ac-settings-label">
                    {TEXT.profile.accountEmail}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="ac-settings-input"
                    placeholder="name@acetime.at"
                  />
                </div>

                <div className="ac-settings-infofield">
                  <label className="ac-settings-label">
                    {TEXT.profile.role}
                  </label>
                  <div className="ac-settings-staticbox">{role}</div>
                </div>

                <div className="ac-settings-infofield">
                  <label className="ac-settings-label">
                    {TEXT.profile.memberSince}
                  </label>
                  <div className="ac-settings-staticbox">{memberSince}</div>
                </div>
              </div>

              <div className="ac-settings-actions-row">
                <button
                  type="button"
                  onClick={saveProfile}
                  className="ac-primarybtn ac-settings-smallbtn"
                >
                  {TEXT.profile.saveButton}
                </button>
              </div>
            </div>
          </div>

          {renderTeacherClassSection}
        </div>
      );
    }

    if (activeTab === "sicherheit") {
      const isAdminRole = role === "admin";

      return (
        <div className="ac-settings-security-grid">
          <div className="ac-settings-card">
            <div className="ac-settings-card-head">
              <div>
                <div className="ac-settings-card-title">
                  {TEXT.security.passwordProtectionTitle}
                </div>
                <div className="ac-settings-card-text">
                  {TEXT.security.passwordProtectionText}
                </div>
              </div>

              <div className="ac-settings-card-badge">
                {TEXT.tabs.sicherheit.navLabel}
              </div>
            </div>

            <div className="ac-settings-safebox">{TEXT.security.safetyHint}</div>

            {isAdminRole ? (
              <>
                <div className="ac-settings-passwordgrid" style={{ marginTop: "16px" }}>
                  <div className="ac-settings-field ac-settings-passwordfield-current">
                    <label className="ac-settings-label">
                      {TEXT.security.currentPassword}
                    </label>
                    <div className="ac-settings-passwordwrap">
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="ac-settings-input ac-settings-input-withbtn"
                        placeholder={TEXT.security.currentPasswordPlaceholder}
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword((value) => !value)}
                        className="ac-settings-passwordbtn"
                        title={showCurrentPassword ? TEXT.common.hide : TEXT.common.show}
                      >
                        {showCurrentPassword ? "🙈" : "👁️"}
                      </button>
                    </div>
                  </div>

                  <div className="ac-settings-field">
                    <label className="ac-settings-label">
                      {TEXT.security.newPassword}
                    </label>
                    <div className="ac-settings-passwordwrap">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="ac-settings-input ac-settings-input-withbtn"
                        placeholder={TEXT.security.newPasswordPlaceholder}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword((value) => !value)}
                        className="ac-settings-passwordbtn"
                        title={showNewPassword ? TEXT.common.hide : TEXT.common.show}
                      >
                        {showNewPassword ? "🙈" : "👁️"}
                      </button>
                    </div>
                  </div>

                  <div className="ac-settings-field">
                    <label className="ac-settings-label">
                      {TEXT.security.confirmPassword}
                    </label>
                    <div className="ac-settings-passwordwrap">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`ac-settings-input ac-settings-input-withbtn ${
                          confirmMismatch ? "is-warning" : ""
                        }`}
                        placeholder={TEXT.security.confirmPasswordPlaceholder}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((value) => !value)}
                        className="ac-settings-passwordbtn"
                        title={showConfirmPassword ? TEXT.common.hide : TEXT.common.show}
                      >
                        {showConfirmPassword ? "🙈" : "👁️"}
                      </button>
                    </div>

                    {confirmMismatch && (
                      <div className="ac-settings-passwordhint is-warning">
                        {TEXT.security.confirmMismatch}
                      </div>
                    )}
                  </div>
                </div>

                <div className="ac-settings-forgotline">
                  <button
                    type="button"
                    onClick={sendPasswordResetFromSettings}
                    className="ac-settings-linkbtn"
                  >
                    {TEXT.security.forgotPassword}
                  </button>
                </div>

                <div className="ac-settings-actions-row">
                  <button
                    type="button"
                    onClick={savePassword}
                    className="ac-primarybtn ac-settings-smallbtn"
                  >
                    {TEXT.security.saveButton}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setCurrentPassword("");
                      setNewPassword("");
                      setConfirmPassword("");
                    }}
                    className="ac-ghostbtn ac-settings-smallbtn"
                  >
                    {TEXT.common.cancel}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="ac-settings-safebox" style={{ marginTop: "12px" }}>
                  <div style={{ fontWeight: 800, marginBottom: "6px" }}>
                    {TEXT.security.forgotPassword}
                  </div>
                  <div>{TEXT.security.forgotPasswordHint}</div>
                  <div style={{ marginTop: "6px", opacity: 0.9 }}>
                    {TEXT.security.forgotPasswordInfo}
                  </div>
                </div>

                <div className="ac-settings-forgotline">
                  <button
                    type="button"
                    onClick={sendPasswordResetFromSettings}
                    className="ac-settings-linkbtn"
                  >
                    {TEXT.security.forgotPassword}
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="ac-settings-card">
            <div className="ac-settings-card-head">
              <div>
                <div className="ac-settings-card-title">
                  {isAdminRole ? TEXT.security.securityCheckTitle : "Kontoschutz"}
                </div>
                <div className="ac-settings-card-text">
                  {isAdminRole
                    ? TEXT.security.securityCheckText
                    : "Für Lehrkräfte und Viewer läuft die Passwortänderung sauber über einen Reset-Link an die Konto-E-Mail."}
                </div>
              </div>

              <div className="ac-settings-scorepill">
                {isAdminRole
                  ? formatText(TEXT.security.score, { score: passwordScore })
                  : "Reset-Link"}
              </div>
            </div>

            {isAdminRole ? (
              <div className="ac-settings-rulelist">
                <PasswordRule
                  ok={passwordRules.length}
                  text={TEXT.security.length}
                />
                <PasswordRule
                  ok={passwordRules.number}
                  text={TEXT.security.number}
                />
                <PasswordRule
                  ok={passwordRules.special}
                  text={TEXT.security.special}
                />
                <PasswordRule
                  ok={passwordRules.differentFromCurrent}
                  text={TEXT.security.different}
                />
                <PasswordRule
                  ok={passwordRules.confirm}
                  text={TEXT.security.confirm}
                />
              </div>
            ) : (
              <div className="ac-settings-rulelist">
                <PasswordRule ok text="Reset-Link an Konto-E-Mail senden" />
                <PasswordRule ok text="Passwort sicher direkt über Firebase zurücksetzen" />
                <PasswordRule ok text="Kein aktuelles Passwort im Panel nötig" />
              </div>
            )}
          </div>
        </div>
      );
    }

    if (activeTab === "benachrichtigungen") {
      return (
        <div className="ac-settings-notify-grid">
          <div className="ac-settings-card ac-settings-notify-card-main">
            <div className="ac-settings-card-head">
              <div>
                <div className="ac-settings-card-title">
                  {TEXT.notifications.optionalTitle}
                </div>
                <div className="ac-settings-card-text">
                  {TEXT.notifications.optionalText}
                </div>
              </div>

              <div className="ac-settings-scorepill">
                {formatText(TEXT.notifications.optionalActiveCount, {
                  count: activeOptionalAlerts,
                })}
              </div>
            </div>

            <div className="ac-settings-togglelist">
              <div className="ac-settings-toggleitem">
                <div>
                  <div className="ac-settings-togglelabel">
                    {TEXT.notifications.criticalStressTitle}
                  </div>
                  <div className="ac-settings-togglesub">
                    {TEXT.notifications.criticalStressText}
                  </div>
                </div>
                <Toggle
                  checked={adminAlertsEnabled}
                  onChange={() =>
                    toggleOptionalNotification(
                      TEXT.notifications.criticalStressTitle,
                      adminAlertsEnabled,
                      setAdminAlertsEnabled
                    )
                  }
                />
              </div>

              <div className="ac-settings-toggleitem">
                <div>
                  <div className="ac-settings-togglelabel">
                    {TEXT.notifications.streakTitle}
                  </div>
                  <div className="ac-settings-togglesub">
                    {TEXT.notifications.streakText}
                  </div>
                </div>
                <Toggle
                  checked={streakMailEnabled}
                  onChange={() =>
                    toggleOptionalNotification(
                      TEXT.notifications.streakTitle,
                      streakMailEnabled,
                      setStreakMailEnabled
                    )
                  }
                />
              </div>

              <div className="ac-settings-toggleitem">
                <div>
                  <div className="ac-settings-togglelabel">
                    {TEXT.notifications.summariesTitle}
                  </div>
                  <div className="ac-settings-togglesub">
                    {TEXT.notifications.summariesText}
                  </div>
                </div>
                <Toggle
                  checked={summaryMailEnabled}
                  onChange={() =>
                    toggleOptionalNotification(
                      TEXT.notifications.summariesTitle,
                      summaryMailEnabled,
                      setSummaryMailEnabled
                    )
                  }
                />
              </div>

              <div className="ac-settings-toggleitem">
                <div>
                  <div className="ac-settings-togglelabel">
                    {TEXT.notifications.feedbackTitle}
                  </div>
                  <div className="ac-settings-togglesub">
                    {TEXT.notifications.feedbackText}
                  </div>
                </div>
                <Toggle
                  checked={feedbackMailEnabled}
                  onChange={() =>
                    toggleOptionalNotification(
                      TEXT.notifications.feedbackTitle,
                      feedbackMailEnabled,
                      setFeedbackMailEnabled
                    )
                  }
                />
              </div>

              <div className="ac-settings-toggleitem">
                <div>
                  <div className="ac-settings-togglelabel">
                    {TEXT.notifications.technicalTitle}
                  </div>
                  <div className="ac-settings-togglesub">
                    {TEXT.notifications.technicalText}
                  </div>
                </div>
                <Toggle
                  checked={technicalMailEnabled}
                  onChange={() =>
                    toggleOptionalNotification(
                      TEXT.notifications.technicalTitle,
                      technicalMailEnabled,
                      setTechnicalMailEnabled
                    )
                  }
                />
              </div>
            </div>

            <div className="ac-settings-actions-row">
              <button
                type="button"
                onClick={saveNotifications}
                className="ac-primarybtn ac-settings-smallbtn"
              >
                {TEXT.notifications.saveButton}
              </button>
            </div>
          </div>

          <div className="ac-settings-notify-stack">
            <div className="ac-settings-card">
              <div className="ac-settings-card-head">
                <div>
                  <div className="ac-settings-card-title">
                    {TEXT.notifications.mainAddressTitle}
                  </div>
                  <div className="ac-settings-card-text">
                    {TEXT.notifications.mainAddressText}
                  </div>
                </div>

                <div className="ac-settings-card-badge">
                  {TEXT.notifications.requiredAddress}
                </div>
              </div>

              <div className="ac-settings-field">
                <label className="ac-settings-label">
                  {TEXT.notifications.mainAddressLabel}
                </label>
                <input
                  type="email"
                  value={adminAlertMail}
                  onChange={(e) => setAdminAlertMail(e.target.value)}
                  className="ac-settings-input"
                  placeholder="admin@acetime.app"
                />
              </div>

              <div className="ac-settings-safebox">
                {TEXT.notifications.requiredHint}
              </div>
            </div>

            <div className="ac-settings-card">
              <div className="ac-settings-card-head">
                <div>
                  <div className="ac-settings-card-title">
                    {TEXT.notifications.fixedTitle}
                  </div>
                  <div className="ac-settings-card-text">
                    {TEXT.notifications.fixedText}
                  </div>
                </div>

                <div className="ac-settings-card-badge is-locked">
                  {TEXT.notifications.locked}
                </div>
              </div>

              <div className="ac-settings-fixedlist">
                <div className="ac-settings-fixeditem">
                  <div className="ac-settings-fixedcopy">
                    <div className="ac-settings-fixedtitle">
                      {TEXT.notifications.systemOutageTitle}
                    </div>
                    <div className="ac-settings-fixedtext">
                      {TEXT.notifications.systemOutageText}
                    </div>
                  </div>
                  <div className="ac-settings-fixedbadge">
                    {TEXT.notifications.fixedBadge}
                  </div>
                </div>

                <div className="ac-settings-fixeditem">
                  <div className="ac-settings-fixedcopy">
                    <div className="ac-settings-fixedtitle">
                      {TEXT.notifications.securityIncidentTitle}
                    </div>
                    <div className="ac-settings-fixedtext">
                      {TEXT.notifications.securityIncidentText}
                    </div>
                  </div>
                  <div className="ac-settings-fixedbadge">
                    {TEXT.notifications.fixedBadge}
                  </div>
                </div>

                <div className="ac-settings-fixeditem">
                  <div className="ac-settings-fixedcopy">
                    <div className="ac-settings-fixedtitle">
                      {TEXT.notifications.deliveryIssueTitle}
                    </div>
                    <div className="ac-settings-fixedtext">
                      {TEXT.notifications.deliveryIssueText}
                    </div>
                  </div>
                  <div className="ac-settings-fixedbadge">
                    {TEXT.notifications.fixedBadge}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="ac-settings-card ac-settings-notify-card-wide">
            <div className="ac-settings-card-head">
              <div>
                <div className="ac-settings-card-title">
                  {TEXT.notifications.recipientsTitle}
                </div>
                <div className="ac-settings-card-text">
                  {TEXT.notifications.recipientsText}
                </div>
              </div>

              <div className="ac-settings-card-badge">
                {formatText(TEXT.notifications.recipientsStoredCount, {
                  count: extraRecipients.length,
                })}
              </div>
            </div>

            <div className="ac-settings-inlineadd">
              <input
                type="email"
                value={newRecipientMail}
                onChange={(e) => setNewRecipientMail(e.target.value)}
                className="ac-settings-input"
                placeholder={TEXT.notifications.addRecipientPlaceholder}
              />

              <button
                type="button"
                onClick={addRecipient}
                className="ac-ghostbtn ac-settings-inlineaddbtn"
              >
                {TEXT.common.add}
              </button>
            </div>

            {extraRecipients.length > 0 ? (
              <div className="ac-settings-chiprow">
                {extraRecipients.map((mail) => (
                  <RecipientChip
                    key={mail}
                    mail={mail}
                    onRemove={() => removeRecipient(mail)}
                    removeTitle={TEXT.common.remove}
                  />
                ))}
              </div>
            ) : (
              <div className="ac-settings-mutedbox">
                {TEXT.notifications.noRecipients}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (activeTab === "system") {
  return (
    <div>
      <div className="ac-settings-system-grid ac-settings-system-grid-single">
        <div className="ac-settings-card">
          <div className="ac-settings-card-title">
            {TEXT.system.themeTitle}
          </div>
          <div className="ac-settings-card-text">
            {TEXT.system.themeText}
          </div>

          <div className="ac-settings-optiongrid">
            {themeOptions.map((option) => (
              <button
                key={option}
                type="button"
                onClick={async () => {
                  setThemePreference(option);

                  try {
                    await saveAdminDoc({
                      settings: {
                        system: {
                          theme: option,
                        },
                      },
                    });

                    notify("Design automatisch gespeichert.", "success");
                  } catch (error) {
                    console.error("Auto-save theme failed:", error);
                    notify("Automatisches Speichern fehlgeschlagen. Bitte auf „System speichern“ klicken.", "error");
                  }
                }}
                className={`ac-settings-choice ${
                  themePreference === option ? "is-active" : ""
                }`}
              >
                <span className="ac-settings-choice-title">
                  {themeLabel(option)}
                </span>
                <span className="ac-settings-choice-text">
                  {themeDescription(option)}
                </span>
              </button>
            ))}
          </div>

          <div className="ac-settings-modehint">
            {themePreference === "system"
              ? formatText(TEXT.system.themeResolvedAuto, {
                  theme: resolvedThemeLabel,
                })
              : formatText(TEXT.system.themeResolvedManual, {
                  theme: resolvedThemeLabel,
                })}
          </div>
        </div>
      </div>

      <div className="ac-settings-actions-row">
        <button
          type="button"
          onClick={saveSystemSettings}
          className="ac-primarybtn ac-settings-smallbtn"
        >
          {TEXT.system.saveButton}
        </button>
      </div>
    </div>
  );
}

return null;
  };

  if (loading) {
    return (
      <DashboardLayout title={TEXT.pageTitle}>
        <div className="ac-soft-panel ac-settings-hero">
          <div className="ac-settings-hero-grid">
            <div>
              <h2 className="ac-settings-hero-title">{TEXT.heroTitle}</h2>
              <p className="ac-settings-hero-text">Einstellungen werden geladen…</p>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={TEXT.pageTitle}>
      <div className="space-y-5">
        <section className="ac-soft-panel ac-settings-hero">
          <div className="ac-settings-hero-grid">
            <div>
              <h2 className="ac-settings-hero-title">{TEXT.heroTitle}</h2>
              <p className="ac-settings-hero-text">{TEXT.heroText}</p>
            </div>

            <div className="ac-settings-hero-side">
              <div className="ac-settings-acebox">
                <AceAvatar variant={aceVariant} size={56} />
                <div className="ac-settings-acebubble">{TEXT.aceSays}</div>
              </div>
            </div>
          </div>
        </section>

        <section className="ac-settings-shell">
          <aside className="ac-settings-nav">
            <div className="ac-settings-nav-title">{TEXT.navTitle}</div>

            <div className="ac-settings-nav-list">
              {navItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setActiveTab(item.key)}
                  className={`ac-settings-navbtn ${
                    activeTab === item.key ? "is-active" : ""
                  }`}
                >
                  <span className="ac-settings-navicon">
                    <SettingsNavIcon name={item.key} />
                  </span>

                  <span className="ac-settings-navcopy">
                    <span className="ac-settings-navlabel">{item.title}</span>
                    <span className="ac-settings-navsub">{item.sub}</span>
                  </span>
                </button>
              ))}
            </div>
          </aside>

          <div className="ac-settings-content">
            <div className="ac-settings-panehead">
              <h3 className="ac-settings-panetitle">
                {tabMeta[activeTab].title}
              </h3>
              <p className="ac-settings-panesub">
                {tabMeta[activeTab].subtitle}
              </p>
            </div>

            {renderTabContent()}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}